import { Editor } from '@tiptap/react'
import { Button, Form, Input, Modal, Tooltip } from 'antd'
import { Youtube } from 'iconsax-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  editor: Editor
}

const getYouTubeVideoId = (url: string): string | null => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)

  return match && match[2].length === 11 ? match[2] : null
}

export const YouTubeButton = ({ editor }: Props) => {
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState(false)
  const [form] = Form.useForm()

  const onOpen = () => {
    setIsOpen(true)
    form.resetFields()
  }

  const onFinish = () => {
    const values = form.getFieldsValue()
    const url = values?.url?.trim()

    if (url) {
      const videoId = getYouTubeVideoId(url)
      if (videoId) {
        editor
          .chain()
          .focus()
          .setYoutubeVideo({
            src: `https://www.youtube.com/embed/${videoId}`,
            width: 560,
            height: 315
          })
          .run()
        setIsOpen(false)
        form.resetFields()
      } else {
        form.setFields([
          {
            name: 'url',
            errors: [t('editor.invalid_youtube_url')]
          }
        ])
      }
    }
  }

  const onCancel = () => {
    setIsOpen(false)
    form.resetFields()
  }

  return (
    <>
      <Tooltip title={t('editor.youtube')}>
        <Button icon={<Youtube size={14} />} onClick={onOpen} />
      </Tooltip>

      <Modal
        open={isOpen}
        title={t('editor.insert_youtube_video')}
        onOk={onFinish}
        onCancel={onCancel}
        okText={t('editor.insert')}
        cancelText={t('editor.cancel')}
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="url"
            label={t('editor.youtube_url')}
            rules={[
              {
                required: true,
                message: t('editor.please_enter_youtube_url')
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve()
                  }
                  const videoId = getYouTubeVideoId(value)
                  if (videoId) {
                    return Promise.resolve()
                  }
                  return Promise.reject(
                    new Error(t('editor.invalid_youtube_url'))
                  )
                }
              }
            ]}
          >
            <Input placeholder="https://www.youtube.com/watch?v=..." />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
